import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { Dialog } from 'primeng/dialog';
import { UIChart } from 'primeng/chart';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { AvailabilityReportService } from '../../services/availability-report.service';
import { AllocationService } from '../../services/allocation.service';
import { ForecastService } from '../../services/forecast.service';
import { CategoryService } from '../../services/category.service';
import { AvailabilityReportRow } from '../../models/availability-report.model';
import { Allocation } from '../../models/allocation.model';
import { Forecast } from '../../models/forecast.model';
import { Category } from '../../models/category.model';
import { Employee } from '../../models/employee.model';
import { EmployeeService } from '../../services/employee.service';
import { forkJoin, catchError, of } from 'rxjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-availability-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Select,
    Dialog,
    UIChart,
    Tooltip
  ],
  templateUrl: './availability-report.component.html',
  styleUrl: './availability-report.component.scss'
})
export class AvailabilityReportComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  reportData: AvailabilityReportRow[] = [];
  filteredReportData: AvailabilityReportRow[] = [];
  loading: boolean = false;
  selectedYear: number;

  yearOptions: DropdownOption[] = [];

  // Chart data
  chartData: any = {
    labels: [],
    datasets: []
  };
  chartOptions: any;
  displayChartDialog: boolean = false;
  chartLoading: boolean = false;

  // Employees for chart (used for category lookup / active status)
  employees: Employee[] = [];
  
  // Category filter for chart
  categories: Category[] = [];
  categoryOptions: { label: string; value: string | null }[] = [];
  selectedCategoryId: string | null = null;
  
  // Forecasts for chart and tooltip name lookup
  forecasts: Forecast[] = [];
  private chartDataYearLoaded: number | null = null;

  // categoryId → monthKey → sorted employee names (built from forecasts)
  private availableNamesByCategory  = new Map<string, Map<string, string[]>>();
  private allocatedNamesByCategory  = new Map<string, Map<string, string[]>>();

  // Month columns for display
  monthColumns = [
    { key: 'jan', label: 'Jan' },
    { key: 'feb', label: 'Feb' },
    { key: 'mar', label: 'Mar' },
    { key: 'apr', label: 'Apr' },
    { key: 'may', label: 'May' },
    { key: 'jun', label: 'Jun' },
    { key: 'jul', label: 'Jul' },
    { key: 'aug', label: 'Aug' },
    { key: 'sep', label: 'Sep' },
    { key: 'oct', label: 'Oct' },
    { key: 'nov', label: 'Nov' },
    { key: 'dec', label: 'Dec' }
  ];

  constructor(
    private availabilityReportService: AvailabilityReportService,
    private allocationService: AllocationService,
    private forecastService: ForecastService,
    private categoryService: CategoryService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    private pageHeaderService: PageHeaderSearchActionService,
    private yearMonthFilterService: YearMonthFilterService
  ) {
    // Initialize from shared year service so header year selector is in sync
    this.selectedYear = this.yearMonthFilterService.currentYear;
    this.initializeChartOptions();
  }

  get totalCategoriesCount(): number {
    return this.reportData.length;
  }

  get currentMonthAvailable(): number {
    const totalRow = this.filteredReportData.find(r => r.categoryId === 'total');
    if (!totalRow) return 0;
    const monthKeys: (keyof AvailabilityReportRow)[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const currentMonth = new Date().getMonth();
    const val = totalRow[monthKeys[currentMonth]] as number | null;
    return val ?? 0;
  }

  get showingCount(): number {
    return this.filteredReportData.filter(r => r.categoryId !== 'total').length;
  }

  get highlightedMonthsCount(): number {
    const totalRow = this.filteredReportData.find(r => r.categoryId === 'total');
    if (!totalRow) return 0;
    const monthKeys: (keyof AvailabilityReportRow)[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    return monthKeys.filter(k => {
      const v = totalRow[k] as number | null;
      return v !== null && v > 0;
    }).length;
  }

  ngOnInit() {
    // Sync selectedYear with app header year selector
    this.yearMonthFilterService.year.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(y => {
      if (this.selectedYear !== y) {
        this.selectedYear = y;
        this.loadReport();
        this.preloadChartData(); // refresh cache when year changes
      }
    });
    // View Chart primary action
    this.pageHeaderService.setPrimaryButtonLabel('View Chart');
    this.pageHeaderService.setPrimaryButtonIcon('pi pi-chart-line');
    this.pageHeaderService.setCanShowPrimaryAction(true);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.toggleChart();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });
    this.initializeYearOptions();
    this.loadCategories();
    this.loadReport();
    this.preloadChartData(); // warm up chart data in background
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadCategories(): void {
    this.categoryService.getAllCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (categories) => {
        this.categories = categories || [];
        this.categoryOptions = [
          { label: 'All Categories', value: null },
          ...this.categories
            .filter(cat => cat.isActive)
            .map(cat => ({ label: cat.name, value: cat.id }))
        ];
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.categories = [];
        this.categoryOptions = [{ label: 'All Categories', value: null }];
      }
    });
  }

  private initializeYearOptions(): void {
    const years: DropdownOption[] = [];
    // Show years from 2026 onwards for a few years
    for (let year = 2026; year <= 2030; year++) {
      years.push({ label: year.toString(), value: year });
    }
    this.yearOptions = years;
  }

  onYearChange() {
    this.loadReport();
  }

  loadReport() {
    this.loading = true;
    forkJoin([
      this.availabilityReportService.getAvailabilityReport(this.selectedYear),
      this.forecastService.getForecastsByYear(this.selectedYear).pipe(catchError(() => of([] as Forecast[]))),
      this.employees.length > 0
        ? of(this.employees)
        : this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[])))
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([data, forecasts, employees]) => {
        this.reportData = data || [];
        this.forecasts = forecasts || [];
        this.employees = (employees as Employee[]) || [];
        this.chartDataYearLoaded = this.selectedYear;
        this.addTotalRow();
        this.buildAvailableNamesLookup();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading availability report:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message,
          url: error.url
        });
        
        let errorMessage = 'Failed to load availability report. Please try again.';
        
        if (error.status === 422) {
          // Try to get detailed error message from backend
          const details = error.error?.details || error.error?.message || error.error?.error || error.message;
          errorMessage = details || 'Invalid request. Please check the year and try again.';
          
          // Log full error for debugging
          console.error('422 Unprocessable Entity - Full error response:', error.error);
        } else if (error.status === 404) {
          errorMessage = 'Report data not found for the selected year.';
        } else if (error.status === 403 || error.status === 401) {
          errorMessage = 'You do not have permission to view this report.';
        } else if (error.status === 0) {
          errorMessage = 'Unable to connect to the server. Please check if the backend is running.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
        this.loading = false;
        this.reportData = [];
        this.filteredReportData = [];
      }
    });
  }

  private addTotalRow() {
    const totalRow: AvailabilityReportRow = {
      categoryId: 'total',
      categoryName: 'Total',
      jan: this.calculateTotal('jan'),
      feb: this.calculateTotal('feb'),
      mar: this.calculateTotal('mar'),
      apr: this.calculateTotal('apr'),
      may: this.calculateTotal('may'),
      jun: this.calculateTotal('jun'),
      jul: this.calculateTotal('jul'),
      aug: this.calculateTotal('aug'),
      sep: this.calculateTotal('sep'),
      oct: this.calculateTotal('oct'),
      nov: this.calculateTotal('nov'),
      dec: this.calculateTotal('dec')
    };
    
    this.filteredReportData = [...this.reportData, totalRow];
  }

  private calculateTotal(monthKey: keyof Omit<AvailabilityReportRow, 'categoryId' | 'categoryName'>): number | null {
    const sum = this.reportData.reduce((acc, row) => {
      const value = row[monthKey];
      return acc + (value !== null && value !== undefined ? value : 0);
    }, 0);
    
    return sum > 0 ? sum : null;
  }

  formatValue(value: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return value.toString();
  }

  shouldHighlight(value: number | null): boolean {
    return value !== null && value > 0;
  }

  getCellTooltip(value: number | null, categoryId: string, categoryName: string, monthKey: string, monthLabel: string): string {
    if (value === null || value === undefined || value === 0) {
      return `${categoryName} — ${monthLabel}: No available members`;
    }
    const names = this.availableNamesByCategory.get(categoryId)?.get(monthKey) ?? [];
    if (names.length === 0) {
      return `${categoryName} — ${monthLabel}: ${value} member${value === 1 ? '' : 's'} available`;
    }
    const MAX = 20;
    const listed = names.slice(0, MAX).map(n => `• ${n}`).join('\n');
    const overflow = names.length > MAX ? `\n(+${names.length - MAX} more)` : '';
    return `${categoryName} — ${monthLabel} (${names.length} available)\n${listed}${overflow}`;
  }

  private buildAvailableNamesLookup(): void {
    this.availableNamesByCategory.clear();
    this.allocatedNamesByCategory.clear();

    const today = new Date();
    const currentYear  = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-based

    // Mirror the backend rule:
    // Past months  → include all employees (active or inactive) to preserve historical accuracy.
    // Current/future months → include only currently active employees.
    const activeEmployeeIds = new Set(
      this.employees.filter(e => e.isActive).map(e => e.id)
    );
    const employeesLoaded = this.employees.length > 0;

    const isPastMonth = (monthNumber: number): boolean =>
      this.selectedYear < currentYear ||
      (this.selectedYear === currentYear && monthNumber < currentMonth);

    const monthProps: Array<{ key: string; monthNumber: number; prop: keyof Forecast }> = [
      { key: 'jan', monthNumber: 1,  prop: 'janProjectName' },
      { key: 'feb', monthNumber: 2,  prop: 'febProjectName' },
      { key: 'mar', monthNumber: 3,  prop: 'marProjectName' },
      { key: 'apr', monthNumber: 4,  prop: 'aprProjectName' },
      { key: 'may', monthNumber: 5,  prop: 'mayProjectName' },
      { key: 'jun', monthNumber: 6,  prop: 'junProjectName' },
      { key: 'jul', monthNumber: 7,  prop: 'julProjectName' },
      { key: 'aug', monthNumber: 8,  prop: 'augProjectName' },
      { key: 'sep', monthNumber: 9,  prop: 'sepProjectName' },
      { key: 'oct', monthNumber: 10, prop: 'octProjectName' },
      { key: 'nov', monthNumber: 11, prop: 'novProjectName' },
      { key: 'dec', monthNumber: 12, prop: 'decProjectName' },
    ];

    const addToMap = (
      map: Map<string, Map<string, string[]>>,
      categoryId: string,
      key: string,
      name: string
    ) => {
      for (const bucket of [categoryId, 'total']) {
        if (!bucket) continue;
        if (!map.has(bucket)) map.set(bucket, new Map());
        const m = map.get(bucket)!;
        if (!m.has(key)) m.set(key, []);
        m.get(key)!.push(name);
      }
    };

    for (const forecast of this.forecasts) {
      const categoryId = forecast.categoryId ?? '';
      const name = (forecast.employeeName ?? 'Unknown').trim();
      const isActive = !employeesLoaded || activeEmployeeIds.has(forecast.employeeId);

      for (const { key, monthNumber, prop } of monthProps) {
        // Past months: show all employees (even now-inactive) for historical accuracy.
        // Current/future months: skip inactive employees to match backend count.
        if (!isPastMonth(monthNumber) && !isActive) {
          continue;
        }

        const projectName = forecast[prop] as string | undefined;
        if (this.isAvailableStatus(projectName)) {
          addToMap(this.availableNamesByCategory, categoryId, key, name);
        } else if (!this.shouldSkip(projectName)) {
          addToMap(this.allocatedNamesByCategory, categoryId, key, name);
        }
      }
    }

    const sortMap = (map: Map<string, Map<string, string[]>>) =>
      map.forEach(m => m.forEach((v, k) => m.set(k, v.slice().sort())));
    sortMap(this.availableNamesByCategory);
    sortMap(this.allocatedNamesByCategory);
  }

  exportToExcel() {
    try {
      // Prepare data for Excel export
      const exportData = this.filteredReportData.map((row) => {
        const rowData: any = {
          'Category': row.categoryName || ''
        };
        
        // Add month columns
        this.monthColumns.forEach(month => {
          const value = row[month.key as keyof AvailabilityReportRow] as number | null;
          rowData[month.label] = this.formatValue(value);
        });
        
        return rowData;
      });

      // Create worksheet
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 25 },  // Category
        { wch: 12 },  // Jan
        { wch: 12 },  // Feb
        { wch: 12 },  // Mar
        { wch: 12 },  // Apr
        { wch: 12 },  // May
        { wch: 12 },  // Jun
        { wch: 12 },  // Jul
        { wch: 12 },  // Aug
        { wch: 12 },  // Sep
        { wch: 12 },  // Oct
        { wch: 12 },  // Nov
        { wch: 12 }   // Dec
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Availability Report': worksheet },
        SheetNames: ['Availability Report']
      };

      // Generate Excel file
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Create blob and save file
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `Availability_Report_${this.selectedYear}_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Availability report exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export availability report to Excel'
      });
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private initializeChartOptions() {
    this.chartOptions = {
      clip: false,
      interaction: {
        mode: 'nearest',
        intersect: false
      },
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#000000'
          }
        },
        title: {
          display: true,
          text: 'Allocation vs Availability',
          color: '#000000',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          backgroundColor: 'rgba(255,255,255,0.97)',
          titleColor: '#1a1a1a',
          bodyColor: '#333333',
          borderColor: 'rgba(0,0,0,0.15)',
          borderWidth: 1,
          padding: 10,
          boxPadding: 5,
          callbacks: {
            title: (items: any[]) => items.length ? items[0].label : '',
            label: (item: any) => {
              const v = item.parsed.y;
              return `  ${item.dataset.label}: ${v} employee${v !== 1 ? 's' : ''}`;
            },
            afterLabel: (item: any): string[] => {
              const monthKeys = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
              const monthKey = monthKeys[item.dataIndex];
              const categoryId = this.selectedCategoryId || 'total';
              const isAvail = item.dataset.label === 'Available';
              const names = (isAvail ? this.availableNamesByCategory : this.allocatedNamesByCategory)
                .get(categoryId)?.get(monthKey) ?? [];
              const lines = names.slice(0, 5).map((n: string) => `    • ${n}`);
              if (names.length > 5) lines.push(`    (+${names.length - 5} more)`);
              return lines.length ? lines : ['    No employees'];
            },
            labelColor: (item: any) => ({
              borderColor: item.dataset.borderColor,
              backgroundColor: item.dataset.borderColor,
              borderWidth: 2,
              borderRadius: 2
            })
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Month',
            color: '#000000'
          },
          ticks: {
            color: '#000000'
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Number of Employees',
            color: '#000000'
          },
          beginAtZero: true,
          ticks: {
            stepSize: 10,
            precision: 0,
            color: '#000000',
            callback: function(value: any) {
              if (Number.isInteger(value)) {
                return value;
              }
              return '';
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      layout: {
        padding: { top: 10, right: 150, bottom: 5, left: 10 }
      },
      responsive: true,
      maintainAspectRatio: false
    };
  }

  private loadChartDataForYear() {
    if (!this.selectedYear) {
      this.chartData = { labels: [], datasets: [] };
      return;
    }
    // Forecasts already loaded for this year (by loadReport) — skip API call
    if (this.chartDataYearLoaded === this.selectedYear && this.forecasts.length > 0) {
      this.buildChartDataForYear();
      return;
    }
    this.forecastService.getForecastsByYear(this.selectedYear).pipe(
      catchError(() => of([] as Forecast[]))
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (forecasts) => {
        this.forecasts = forecasts || [];
        this.chartDataYearLoaded = this.selectedYear;
        this.buildAvailableNamesLookup();
        this.buildChartDataForYear();
        this.chartLoading = false;
      },
      error: (error) => {
        console.error('Error loading chart data:', error);
        this.forecasts = [];
        this.chartDataYearLoaded = null;
        this.buildChartDataForYear();
        this.chartLoading = false;
      }
    });
  }

  /**
   * Check if a project name represents "Available" status
   */
  private isAvailableStatus(projectName: string | undefined | null): boolean {
    if (!projectName) return false;
    return projectName.trim().toLowerCase() === 'available';
  }

  /**
   * Check if an entry should be skipped (not active employee - "-" or empty)
   */
  private shouldSkip(projectName: string | undefined | null): boolean {
    if (!projectName) return true;
    const trimmed = projectName.trim();
    return trimmed === '' || trimmed === '-';
  }

  private buildChartDataForYear() {
    // Get month property name from forecast model - use ProjectName (not ProjectId)
    const monthPropertyMap: { [key: number]: keyof Forecast } = {
      1: 'janProjectName',
      2: 'febProjectName',
      3: 'marProjectName',
      4: 'aprProjectName',
      5: 'mayProjectName',
      6: 'junProjectName',
      7: 'julProjectName',
      8: 'augProjectName',
      9: 'sepProjectName',
      10: 'octProjectName',
      11: 'novProjectName',
      12: 'decProjectName'
    };

    const monthLabels = this.monthColumns.map(m => m.label);

    const forecastsToUse = this.selectedCategoryId
      ? this.forecasts.filter(f => f.categoryId === this.selectedCategoryId)
      : this.forecasts;

    const labels: string[] = [];
    const availableData: number[] = [];
    const allocationData: number[] = [];

    for (let month = 1; month <= 12; month++) {
      const monthProperty = monthPropertyMap[month];
      if (!monthProperty) continue;

      labels.push(monthLabels[month - 1] ?? `M${month}`);

      // Available = count where projectName === "Available"
      const availableCount = forecastsToUse.filter(f => {
        const projectName = f[monthProperty] as string | undefined;
        return this.isAvailableStatus(projectName);
      }).length;
      availableData.push(availableCount);

      // Allocation = count where projectName exists AND is NOT "Available" AND is NOT "-"
      const allocationCount = forecastsToUse.filter(f => {
        const projectName = f[monthProperty] as string | undefined;
        if (this.shouldSkip(projectName)) return false; // Skip "-" or empty
        if (this.isAvailableStatus(projectName)) return false; // Skip "Available"
        return true; // Has a project name (allocated)
      }).length;
      allocationData.push(allocationCount);
    }

    this.chartData = {
      labels,
      datasets: [
        {
          label: 'Allocation',
          data: allocationData,
          borderColor: 'green',
          backgroundColor: 'rgba(0, 128, 0, 0.2)',
          pointBackgroundColor: 'green',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 9,
          pointHitRadius: 20,
          pointHoverBackgroundColor: 'green',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          tension: 0.4,
          fill: false
        },
        {
          label: 'Available',
          data: availableData,
          borderColor: 'red',
          backgroundColor: 'rgba(255, 0, 0, 0.2)',
          pointBackgroundColor: 'red',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 9,
          pointHitRadius: 20,
          pointHoverBackgroundColor: 'red',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          tension: 0.4,
          fill: false
        }
      ]
    };
  }

  onChartCategoryChange() {
    // If data already loaded for this year, just rebuild with the new category filter.
    if (this.chartDataYearLoaded === this.selectedYear && this.forecasts.length > 0) {
      this.buildChartDataForYear();
      return;
    }
    this.loadChartDataForYear();
  }

  private preloadChartData(): void {
    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[]))),
      forecasts: this.forecastService.getForecastsByYear(this.selectedYear).pipe(catchError(() => of([] as Forecast[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ employees, forecasts }) => {
        this.employees = employees || [];
        this.forecasts = forecasts || [];
        this.chartDataYearLoaded = this.selectedYear;
        this.buildAvailableNamesLookup();
        this.buildChartDataForYear();
        this.chartLoading = false;
      },
      error: () => {
        this.chartLoading = false;
      }
    });
  }

  toggleChart() {
    this.displayChartDialog = true;

    // Data already cached for this year — open instantly
    if (this.chartDataYearLoaded === this.selectedYear && this.employees.length > 0) {
      this.chartLoading = false;
      return;
    }

    // Still loading in background — show spinner until preload finishes
    this.chartLoading = true;
  }

  onChartDialogShow(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
  }
}

